/**
 * Cross-Sell Engine — domain module.
 *
 * Construido en modo AISLADO (Sprint 11), activado en Sprint 12 SOLO para la
 * organización de laboratorio — ver application/container.js
 * (REACT_APP_CROSS_SELL_ENABLED). Abel sigue viendo el stub hasta que se
 * decida conectarlo para su organización explícitamente.
 *
 * Reglas de elegibilidad (Sprint 12 — ver evaluateCrossSellEligibility):
 *   1. Prioridad entre motores: Renovación > Recuperación > Venta Cruzada.
 *      Un actor con señal activa de cualquiera de los dos primeros nunca
 *      genera oportunidad de Venta Cruzada, sin importar su potencial.
 *   2. crossSellPotential debe ser un número válido >= MIN_CROSS_SELL_POTENTIAL.
 *      Vacío, no numérico o por debajo del umbral → sin oportunidad.
 *   3. crossSellProduct y currentProduct deben existir con texto útil.
 *   4. crossSellProduct no puede ser (normalizado) igual a currentProduct —
 *      no se ofrece lo que el cliente ya tiene.
 */
import { createOpportunity, createClosure, createOutcome } from "../entities";

const ENGINE_ID = "cross_sell";

/** Umbral mínimo de potencial para considerar candidato (Sprint 12, decisión
 * explícita de Fernando: 70-100 genera oportunidad, 0-69 no). */
export const MIN_CROSS_SELL_POTENTIAL = 70;

/** Normaliza un nombre de producto para comparar sin falsos negativos por
 * mayúsculas, espacios al inicio/final o espacios duplicados internos. */
export function normalizeProductName(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}

/**
 * Evalúa si un actor es elegible para Venta Cruzada, SIN efectos
 * secundarios (no toca storage) — usada tanto por evaluateActor (decide si
 * procede) como por portfolio.js (para reportar la razón exacta de
 * exclusión en el resultado de la importación).
 * @returns {{eligible: boolean, reasons: string[]}}
 */
export function evaluateCrossSellEligibility(actor) {
  const attrs = actor.verticalAttributes || {};

  if (attrs.daysUntilRenewal != null) {
    return { eligible: false, reasons: ["Señal de Renovación activa — tiene prioridad sobre Venta Cruzada."] };
  }
  if (attrs.monthsInactive != null && attrs.monthsInactive >= 2) {
    return { eligible: false, reasons: ["Señal de Recuperación activa — tiene prioridad sobre Venta Cruzada."] };
  }

  const potential = attrs.crossSellPotential;
  const hasValidPotential = typeof potential === "number" && !Number.isNaN(potential);
  if (!hasValidPotential) {
    return { eligible: false, reasons: ["Sin señal de venta cruzada (crossSellPotential vacío)."] };
  }

  const suggested = attrs.crossSellProduct;
  const current = attrs.currentProduct;
  const hasSuggested = typeof suggested === "string" && suggested.trim() !== "";
  const hasCurrent = typeof current === "string" && current.trim() !== "";

  const reasons = [];
  if (potential < MIN_CROSS_SELL_POTENTIAL) {
    reasons.push(`Potencial (${potential}) por debajo del umbral (${MIN_CROSS_SELL_POTENTIAL}).`);
  }
  if (!hasSuggested) reasons.push("Producto sugerido ausente.");
  if (!hasCurrent) reasons.push("Producto actual ausente.");
  if (hasSuggested && hasCurrent && normalizeProductName(suggested) === normalizeProductName(current)) {
    reasons.push("El cliente ya tiene el producto sugerido.");
  }

  return { eligible: reasons.length === 0, reasons };
}

/**
 * @param {{
 *   storage: import('../ports').StoragePort,
 *   intelligence: import('../ports').IntelligencePort,
 *   llm: import('../ports').LLMPort,
 *   knowledge: import('../ports').KnowledgePort,
 *   crm: import('../ports').CRMPort,
 * }} deps
 */
export function createCrossSellEngine(deps) {
  const { storage, intelligence, llm, knowledge, crm } = deps;

  return {
    id: ENGINE_ID,

    async evaluateActor(actor) {
      const { eligible } = evaluateCrossSellEligibility(actor);
      // Sin elegibilidad clara (umbral, productos, prioridad de motores),
      // el actor no es candidato — no se inventa una oportunidad donde no
      // hay dato real que la sostenga.
      if (!eligible) return;

      const signals = await storage.listSignals(actor.id);
      const { score, priority, probability, windowDays } = await intelligence.scoreActor({
        actor, signals, engineId: ENGINE_ID, vertical: actor.vertical,
      });
      const estimatedValue = await intelligence.estimateValue({
        actor, engineId: ENGINE_ID, vertical: actor.vertical, probability,
      });
      const product = actor.verticalAttributes.crossSellProduct;
      const current = actor.verticalAttributes.currentProduct;
      // ID determinístico por actor+motor — mismo fix que renewal.js (ver
      // comentario ahí): evita duplicar la oportunidad en cada
      // reimportación del mismo actor, y preserva progreso real si ya
      // existía.
      const oppId = `opp-${actor.id}-${ENGINE_ID}`;
      const existing = await storage.getOpportunity(oppId);
      const opp = createOpportunity({
        id: oppId,
        actorId: actor.id,
        engineId: ENGINE_ID,
        companyName: actor.companyName,
        contactName: actor.contactName,
        priority,
        recoveryScore: score,
        estimatedValue,
        probability,
        windowDays,
        windowClosesAt: new Date(Date.now() + windowDays * 24 * 3600 * 1000).toISOString(),
        lastSignalLabel: `Listo para ${product} (tiene: ${current})`,
        // Sin esto, createOpportunity() defaultea isDemo:true incluso para
        // datos reales (hallazgo real de la validación manual, Sprint 12) —
        // hereda el isDemo del actor en vez de asumir uno.
        isDemo: actor.isDemo ?? false,
        ...(existing
          ? { responseStatus: existing.responseStatus, outcome: existing.outcome, notes: existing.notes }
          : {}),
      });
      await storage.saveOpportunity(opp);

      const knowledgeSnippets = await knowledge.getKnowledgeFor({
        actor, engineId: ENGINE_ID, purpose: "play_generation",
      });
      const play = await llm.generatePlay({
        actor, opportunity: opp, signals, engineId: ENGINE_ID,
        vertical: actor.vertical, knowledgeSnippets,
      });
      await storage.savePlay(play);
      return opp;
    },

    // Sin efectos secundarios — portfolio.js la usa para reportar, en el
    // resultado de la importación, POR QUÉ un actor no generó oportunidad
    // de Venta Cruzada (Sprint 12: "razones visibles", nunca silencio).
    explainEligibility(actor) {
      return evaluateCrossSellEligibility(actor);
    },

    // Read APIs — idénticos en forma a recovery.js/renewal.js.
    async listOpportunities(filter = {}) {
      return storage.listOpportunities({ ...filter, engineId: ENGINE_ID });
    },
    async getOpportunity(id) {
      const opp = await storage.getOpportunity(id);
      if (!opp || opp.engineId !== ENGINE_ID) return null;
      const actor = await storage.getActor(opp.actorId);
      const signals = await storage.listSignals(opp.actorId);
      const play = await storage.getLatestPlay(id);
      return { opportunity: opp, actor, signals, play };
    },

    // Mutations — misma regla de idempotencia en "won".
    async recordOutcome(id, { result, notes, closedValueAmount }) {
      const previous = await storage.getOpportunity(id);
      const previousResult = previous?.outcome;
      const opp = await storage.updateOpportunity(id, { outcome: result, notes });
      const outcome = createOutcome({
        opportunityId: id,
        result,
        notes,
        closedValue: result === "won"
          ? { amount: closedValueAmount ?? opp.estimatedValue.value.amount, currency: "USD" }
          : undefined,
      });
      await storage.saveOutcome(outcome);
      if (result === "won" && previousResult !== "won") {
        const closure = createClosure({
          actorId: opp.actorId,
          companyName: opp.companyName,
          engineId: ENGINE_ID,
          closedValue: outcome.closedValue,
          closedByName: "Director Comercial",
        });
        await storage.saveClosure(closure);
        const team = await storage.listTeamMembers();
        const me = team.find((m) => m.id === "user-me");
        if (me) {
          await storage.saveTeamMember({
            ...me,
            monthClosedValue: {
              amount: me.monthClosedValue.amount + outcome.closedValue.amount,
              currency: "USD",
            },
            monthClosuresCount: me.monthClosuresCount + 1,
          });
        }
        crm.pushOutcome(outcome).catch(() => {});
      }
      return opp;
    },

    async regeneratePlay(opportunityId) {
      const opp = await storage.getOpportunity(opportunityId);
      const actor = await storage.getActor(opp.actorId);
      const signals = await storage.listSignals(opp.actorId);
      const knowledgeSnippets = await knowledge.getKnowledgeFor({
        actor, engineId: ENGINE_ID, purpose: "play_generation",
      });
      const play = await llm.generatePlay({
        actor, opportunity: opp, signals, engineId: ENGINE_ID,
        vertical: actor.vertical, knowledgeSnippets,
      });
      await storage.savePlay(play);
      return play;
    },

    async updatePlayBody(opportunityId, suggestedBody) {
      const current = await storage.getLatestPlay(opportunityId);
      if (!current) throw new Error("No play to update");
      const updated = { ...current, suggestedBody, generatedAt: new Date().toISOString() };
      await storage.savePlay(updated);
      return updated;
    },

    async getMetrics() {
      const opps = await storage.listOpportunities({ engineId: ENGINE_ID, limit: 10000 });
      const pending = opps.filter((o) => o.outcome === "pending");
      const won = opps.filter((o) => o.outcome === "won");
      const lost = opps.filter((o) => o.outcome === "lost");
      const closures = await storage.listClosures({ engineId: ENGINE_ID, limit: 10000 });
      const awakeValue = closures.reduce((s, c) => s + c.closedValue.amount, 0);
      const dormantValue = pending.reduce((s, o) => s + o.estimatedValue.value.amount, 0);
      const winRate =
        won.length + lost.length > 0 ? won.length / (won.length + lost.length) : 0;
      return {
        dormantValue: { amount: dormantValue, currency: "USD" },
        awakeValue: { amount: awakeValue, currency: "USD" },
        opportunitiesPending: pending.length,
        opportunitiesWon: won.length,
        opportunitiesLost: lost.length,
        winRate,
        avgWindowDays: Math.round(
          pending.reduce((s, o) => s + o.windowDays, 0) / Math.max(pending.length, 1)
        ),
        priorityBreakdown: {
          high: pending.filter((o) => o.priority === "high").length,
          medium: pending.filter((o) => o.priority === "medium").length,
          low: pending.filter((o) => o.priority === "low").length,
        },
      };
    },

    async getSummary() {
      const m = await this.getMetrics();
      const opps = await storage.listOpportunities({ engineId: ENGINE_ID, limit: 10000 });
      const actorsCount = new Set(opps.map((o) => o.actorId)).size;
      return {
        id: ENGINE_ID,
        label: "Cross-Sell",
        question: "¿A qué cliente puedo venderle algo que ya está listo para comprar?",
        status: "live", // se ignora hasta que config/engines.js también diga "live"
        potentialValue: {
          label: "commercial_value_estimated",
          value: m.dormantValue,
          confidence: 0.65,
          sampleSize: m.opportunitiesPending,
          errorMarginPct: 18,
          rationale:
            "Suma del valor estimado de oportunidades abiertas en Venta Cruzada. " +
            "Se recalcula con cada outcome registrado.",
          lastRecalculatedAt: new Date().toISOString(),
        },
        actorsCount,
        opportunitiesCount: opps.length,
        recentClosedValueUsd: m.awakeValue.amount,
      };
    },
  };
}
